import {
  CreateFinchSessionMutation,
  FinchConnectionDetailFragment,
  useAnalytics,
  useCreateFinchSessionMutation,
  useResponseNotification,
} from '@alice-financial/alice-sdk'
import { Button, Typography } from '@alice-financial/pretext-ui'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import * as React from 'react'
import { FinchConnectResultAlert } from '../../externalServices/finch/FinchConnectResultAlert'
import { NeedingAuthFinchConnections } from './NeedingAuthFinchConnections'

type FinchNotConnectedProps = {
  organizationName: string
  organizationId: number
  needingAuthFinchConnections: FinchConnectionDetailFragment[]
  allFinchConnections: FinchConnectionDetailFragment[]
  isFinchIframeOpened: boolean
  onFinchSessionCreated: (finchSessionInfo: { finchSessionId: string; finchConnectionId: number }) => void
}

export const FinchNotConnected = ({
  organizationName,
  organizationId,
  needingAuthFinchConnections,
  allFinchConnections,
  isFinchIframeOpened,
  onFinchSessionCreated,
}: FinchNotConnectedProps) => {
  const analytics = useAnalytics()
  const { notifyError } = useResponseNotification()
  // Track which specific connection is being processed for per-button loading states
  const [inProgressFinchConnectionId, setInProgressFinchConnectionId] = React.useState<number | null>(null)

  const { mutate: createFinchSession, isLoading: isCreatingSession } = useCreateFinchSessionMutation({
    onSuccess: (data: CreateFinchSessionMutation) => {
      const finchConnection = data.createFinchSession?.finchConnection
      const finchSessionId = finchConnection?.sessionId
      const finchConnectionId = finchConnection?.id

      if (finchSessionId && finchConnectionId) {
        onFinchSessionCreated({ finchSessionId, finchConnectionId })
      }
    },
    onError: (error: Error) => {
      console.error('Failed to create Finch session:', error)
      notifyError(
        'Unable to connect to payroll provider. Please try again or contact support if the issue persists.'
      )
      setInProgressFinchConnectionId(null)
    },
  })

  const onConnectFirst = () => {
    analytics.track('finch_connect.connect_first.clicked')
    createFinchSession({
      input: {
        organizationId,
        displayLabel: `${organizationName} Payroll ${allFinchConnections.length + 1}`,
      },
    })
  }

  const onResumeConnection = (connectionId: number) => {
    analytics.track('finch_connect.resume_connection.clicked')
    setInProgressFinchConnectionId(connectionId)
    createFinchSession({
      input: {
        organizationId,
        finchConnectionId: connectionId,
      },
    })
  }

  return (
    <>
      <FinchConnectResultAlert />
      <Typography variant="h3" color="text.secondary" gutterBottom>
        Grant payroll view permission
      </Typography>

      <Typography variant="body2">
        First, grant Alice <strong>read-only</strong> access to your payroll system so that we can find
        employee information and confirm that benefits are processed.
      </Typography>

      <NeedingAuthFinchConnections
        needingAuthFinchConnections={needingAuthFinchConnections}
        inProgressFinchConnectionId={inProgressFinchConnectionId}
        onResumeConnection={onResumeConnection}
      />

      {needingAuthFinchConnections.length === 0 && (
        <Button
          onClick={onConnectFirst}
          variant="contained"
          color="primary"
          sx={{ my: 1, textWrap: 'nowrap' }}
          endIcon={<OpenInNewIcon />}
          disabled={isCreatingSession || isFinchIframeOpened}
        >
          {isCreatingSession || isFinchIframeOpened ? 'Connecting...' : 'Grant read access'}
        </Button>
      )}
    </>
  )
}
